import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserType } from 'src/types';
import { useDispatch, useSelector } from 'react-redux';
import { uploadUserFile, deleteUserFile, loadUser } from 'src/redux/user/actions';
import { selectSuccessUploadingUserFile, selectUploadingUserFile, selectSuccessDeleteUserFile, selectDeleteUserFile } from 'src/redux/user/selectors';
import ImageUploadLayout from '../ImageUploadLayout';

type Props = {
  user: UserType;
  onClose: () => void;
};

const UploadAvatarModal = ({ user, onClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isEditMode, setIsEditMode] = useState(false);
  const [slideValue, setSlideValue] = useState(10);
  const [preview, setPreview] = useState('');
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const cropRef = useRef(null);
  const displayName = `${user.firstName ?? 'New'} ${user.lastName ?? 'User'}`;
  const uploadProfilePictureSuccess = useSelector(selectSuccessUploadingUserFile);
  const uploadingProfilePicture = useSelector(selectUploadingUserFile);
  const deleteProfilePictureSuccess = useSelector(selectSuccessDeleteUserFile);
  const deletingProfilePicture = useSelector(selectDeleteUserFile);

  const handleSave = async () => {
    if (cropRef) {
      // @ts-ignore
      const dataUrl = cropRef?.current.getImage().toDataURL();
      const result = await fetch(dataUrl);
      const blob = await result.blob();
      dispatch(uploadUserFile.init({ userUuid: user.uuid, file: blob, type: 'profile_picture' }));
    }
  };

  const handleDeleteImg = () => {
    dispatch(deleteUserFile.init({ id: user.attachments?.profile_picture?.id }));
  };

  useEffect(() => {
    if (uploadProfilePictureSuccess) {
      dispatch(uploadUserFile.reset());
      setIsEditMode(false);
      onClose();
    }
    if (deleteProfilePictureSuccess) {
      dispatch(deleteUserFile.reset());
      dispatch(loadUser.init());
      setIsEditMode(false);
      setShowDeletePopup(false);
      onClose();
    }
  }, [uploadProfilePictureSuccess, deleteProfilePictureSuccess]);

  useEffect(() => {
    if (user.attachments?.profile_picture) {
      setPreview(user.attachments?.profile_picture?.location);
    }
  }, [user.attachments]);

  return (
    <ImageUploadLayout
      onClose={onClose}
      imageName={t('profilePage.profilePicture')}
      preview={preview}
      slideValue={slideValue}
      cropRef={cropRef}
      displayName={displayName}
      handleSave={handleSave}
      setShowDeletePopup={setShowDeletePopup}
      setSlideValue={setSlideValue}
      uploadingImage={uploadingProfilePicture}
      deletingImage={deletingProfilePicture}
      showDeletePopup={showDeletePopup}
      handleDeleteImg={handleDeleteImg}
      isEditMode={isEditMode}
      setIsEditMode={setIsEditMode}
      deleteImgSubTitle={t('profilePage.deleteImgSubTitle')}
      deleteImgTitle={t('profilePage.deleteImgTitle')}
      deleteText={t('profilePage.deletePhoto')}
      changeText={t('profilePage.changePhoto')}
      uploadText={t('profilePage.uploadPhoto')}
    />
  );
};

export default UploadAvatarModal;
